/**
 * Helpers for reading the active theme's canvas palette out of CSS custom
 * properties. Canvas elements cannot inherit colour-like CSS, so the spectrum
 * plot and timeline read these tokens once per draw frame and feed them to
 * ``ctx.strokeStyle`` / ``ctx.fillStyle``.
 */

import type { LineSolo } from "../domain/lines";

export interface CanvasPalette {
  plotBg: string;
  timelineBg: string;
  grid: string;
  axisText: string;
  frame: string;
  zeroLine: string;
  emGuide: string;
  gap: string;

  timelineAxis: string;
  timelineTick: string;
  timelineCursorModern: string;
  timelineCursorInterp: string;
  timelineTagBg: string;
  timelineTagText: string;
  timelineTagShadow: string;
  timelineSelectionRing: string;
  timelineCompareRing: string;

  reddened: string;

  featureKnown: string;
  featureDiscovery: string;

  soloFill: Record<LineSolo, string>;
}

function readVar(styles: CSSStyleDeclaration, name: string, fallback: string): string {
  const value = styles.getPropertyValue(name).trim();
  return value === "" ? fallback : value;
}

/**
 * Snapshot the current theme's canvas palette. Call once per draw frame so
 * theme switches take effect on the next render without any manual cache
 * invalidation.
 */
export function readCanvasPalette(): CanvasPalette {
  const styles = getComputedStyle(document.documentElement);
  return {
    plotBg: readVar(styles, "--canvas-plot-bg", "#fbfcff"),
    timelineBg: readVar(styles, "--canvas-timeline-bg", "#f6f8ff"),
    grid: readVar(styles, "--canvas-grid", "rgba(15, 23, 42, 0.055)"),
    axisText: readVar(styles, "--canvas-axis-text", "#64748b"),
    frame: readVar(styles, "--canvas-frame", "rgba(15, 23, 42, 0.08)"),
    zeroLine: readVar(styles, "--canvas-zero-line", "rgba(15, 23, 42, 0.12)"),
    emGuide: readVar(styles, "--canvas-em-guide", "rgba(15, 23, 42, 0.1)"),
    gap: readVar(styles, "--canvas-gap", "rgba(239, 68, 68, 0.35)"),

    timelineAxis: readVar(styles, "--timeline-axis", "rgba(15, 23, 42, 0.14)"),
    timelineTick: readVar(styles, "--timeline-tick", "rgba(15, 23, 42, 0.06)"),
    timelineCursorModern: readVar(styles, "--timeline-cursor-modern", "rgba(15, 23, 42, 0.28)"),
    timelineCursorInterp: readVar(styles, "--timeline-cursor-interp", "rgba(59, 130, 246, 0.65)"),
    timelineTagBg: readVar(styles, "--timeline-tag-bg", "rgba(255, 255, 255, 0.98)"),
    timelineTagText: readVar(styles, "--timeline-tag-text", "#1e293b"),
    timelineTagShadow: readVar(styles, "--timeline-tag-shadow", "rgba(15, 23, 42, 0.12)"),
    timelineSelectionRing: readVar(styles, "--timeline-selection-ring", "#1e293b"),
    timelineCompareRing: readVar(styles, "--timeline-compare-ring", "#fbbf24"),

    reddened: readVar(styles, "--reddened-color", "#fbbf24"),

    featureKnown: readVar(styles, "--feature-known", "#7c3aed"),
    featureDiscovery: readVar(styles, "--feature-discovery", "#d97706"),

    soloFill: {
      all: "transparent",
      sweep: "transparent",
      continuum: readVar(styles, "--solo-fill-continuum", "rgba(59, 130, 246, 0.08)"),
      Halpha: readVar(styles, "--solo-fill-halpha", "rgba(239, 68, 68, 0.08)"),
      Hbeta: readVar(styles, "--solo-fill-hbeta", "rgba(59, 130, 246, 0.1)"),
      Hgamma: readVar(styles, "--solo-fill-hgamma", "rgba(139, 92, 246, 0.08)"),
      OIII: readVar(styles, "--solo-fill-oiii", "rgba(16, 185, 129, 0.08)"),
    },
  };
}
